import React, { useState, useEffect } from 'react';
import { Button } from '../../components/button';
import { Dialog, DialogTitle, DialogBody, DialogActions } from '../../components/dialog';

interface AddSpecialtyDialogProps {
  open: boolean;
  onClose: () => void;
  specialtyOptions: Array<{ value: string; label: string }>;
  selectedSpecialties?: string[];
  onChangeSelected?: (selected: string[]) => void;
  onSave?: (selected: string[]) => void;
}

export const AddSpecialtyDialog: React.FC<AddSpecialtyDialogProps> = ({
  open,
  onClose,
  specialtyOptions,
  selectedSpecialties = [],
  onChangeSelected,
  onSave,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState<string[]>(selectedSpecialties);

  // Reset selected state when dialog is closed
  useEffect(() => {
    if (!open) {
      setSelected([]);
      setSearchQuery('');
    }
  }, [open]);

  const filteredSpecialties = specialtyOptions.filter((specialty) =>
    specialty.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleToggle = (value: string) => {
    let newSelected;
    if (selected.includes(value)) {
      newSelected = selected.filter((v) => v !== value);
    } else {
      newSelected = [...selected, value];
    }
    setSelected(newSelected);
    onChangeSelected?.(newSelected);
  };

  const handleSave = () => {
    if (onSave) {
      onSave(selected);
    } else {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} className="tw:max-w-md">
      <DialogTitle className="tw:text-lg tw:font-medium tw:text-zinc-950 tw:dark:!text-white/90 tw:flex tw:items-center tw:justify-between">
        <span>Add Specialty</span>
      </DialogTitle>
      <DialogBody>
        <div className="tw:space-y-4">
          <div className="tw:flex-none tw:py-6 tw:border-b tw:border-t tw:border-zinc-200 tw:dark:border-white/10">
            <input
              type="text"
              placeholder="Search specialties..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-zinc-950/10 tw:rounded-lg tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-brand-green-1 tw:bg-white tw:dark:bg-white/5 tw:dark:border-white/10 tw:dark:text-white/90"
            />
          </div>
          <div className="tw:max-h-96 tw:overflow-y-auto">
            <div className="tw:space-y-2">
              {filteredSpecialties.length > 0 ? (
                filteredSpecialties.map((specialty) => {
                  const isSelected = selected.includes(specialty.value);
                  return (
                    <button
                      key={specialty.value}
                      type="button"
                      className={`tw:w-full tw:text-left tw:p-2 tw:rounded tw:transition-colors tw:flex tw:items-center tw:gap-2 ${
                        isSelected
                          ? 'tw:bg-brand-green-1 tw:text-white'
                          : 'tw:hover:bg-brand-green-0/5 tw:active:bg-brand-green-1 tw:text-zinc-950 tw:dark:text-white/90 tw:dark:hover:bg-brand-green-0/10'
                      }`}
                      onClick={() => handleToggle(specialty.value)}
                    >
                      <span className="tw:flex-1">{specialty.label}</span>
                    </button>
                  );
                })
              ) : (
                <div className="tw:text-center tw:text-zinc-500">No specialties found</div>
              )}
            </div>
          </div>
        </div>
        <DialogActions>
          <Button type="button" plain onClick={onClose}>
            Close
          </Button>
          <Button type="button" color="brandgreen0" onClick={handleSave}>
            Add
          </Button>
        </DialogActions>
      </DialogBody>
    </Dialog>
  );
};
